import { Paper } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { cardShadow, dataTableSx } from "../PredictionTab/colors";

/** Flat row for the mapping grid. `code` is "" when the role_string is unmapped. */
export interface MappingGridRow {
	roleString: string;
	code: string;
}

interface JobMappingGridProps {
	rows: MappingGridRow[];
	/** Dropdown options derived from the EFFECTIVE registry (server jobs + pending edits). */
	jobOptions: { value: string; label: string }[];
	onMappingChange: (roleString: string, code: string | null) => void;
	loading: boolean;
	error: boolean;
}

/**
 * Editable role_string → PBJ job grid. The single editable column is a job picker showing
 * "{code} - {description}" values (never the UUID). Edits are buffered upward — there is no
 * per-cell auto-save; the tab's Save button commits the whole changeset.
 */
export const JobMappingGrid: FC<JobMappingGridProps> = ({ rows, jobOptions, onMappingChange, loading, error }) => {
	const { t } = useTranslation();

	// An explicit "unmapped" option lets the user clear a mapping (code "" → null on save).
	const valueOptions = useMemo(() => [{ value: "", label: t("pbj_model.jobs.unmapped") }, ...jobOptions], [jobOptions, t]);

	const processRowUpdate = useCallback(
		(newRow: MappingGridRow, oldRow: MappingGridRow): MappingGridRow => {
			if (newRow.code !== oldRow.code) {
				onMappingChange(newRow.roleString, newRow.code === "" ? null : newRow.code);
			}
			return newRow;
		},
		[onMappingChange],
	);

	const columns: GridColDef<MappingGridRow>[] = useMemo(
		() => [
			{ field: "roleString", headerName: t("pbj_model.jobs.column.role_string"), flex: 1, minWidth: 200 },
			{
				field: "code",
				headerName: t("pbj_model.jobs.column.pbj_job"),
				flex: 1,
				minWidth: 240,
				editable: true,
				type: "singleSelect",
				valueOptions,
			},
		],
		[t, valueOptions],
	);

	return (
		<Paper
			elevation={0}
			// Fills the tab's height so the grid — not the page — owns the scroll.
			sx={(theme) => ({
				p: { xs: 1.5, md: 2 },
				borderRadius: 3,
				bgcolor: "background.paper",
				boxShadow: cardShadow(theme),
				height: "100%",
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
			})}
		>
			<SystemDataGrid<MappingGridRow>
				rows={rows}
				columns={columns}
				getRowId={(row) => row.roleString}
				searchableColumns={["roleString"]}
				searchPlaceholder={t("pbj_model.jobs.search.role_string")}
				processRowUpdate={processRowUpdate}
				loading={loading}
				error={error}
				errorMessage={t("pbj_model.jobs.load_failed")}
				emptyState={<EmptyState title={t("pbj_model.jobs.empty")} dataTestId="pbj-jobs-mapping-empty" />}
				defaultPageSize={25}
				sx={(theme) => dataTableSx(theme)}
				dataTestId="pbj-jobs-mapping-grid"
			/>
		</Paper>
	);
};
